import * as React from 'react';
import { XIcon } from 'lucide-react';
import { cn } from '@noor/lib/cn';
import { partitionBySize } from '@noor/lib';
import { Button } from './button';
import { DropzoneEmptyFace, DropzoneFilledFace } from './dropzone-faces';

export interface DropzoneProps {
  /** Currently picked files (`File | null` for single, `File[]` for multiple). */
  value: File | File[] | null | undefined;
  /** Reports the next full selection after pick, drop, or remove. */
  onFiles: (files: File[]) => void;
  multiple?: boolean;
  accept?: string;
  /**
   * Size ceiling per file, in bytes. Files over the ceiling are dropped and
   * reported via `onRejected`; when the whole pick is rejected the selection
   * is left untouched.
   */
  maxBytes?: number;
  /** Names of files rejected for exceeding `maxBytes`. */
  onRejected?: (fileNames: string[]) => void;
  /** Dropzone title (20px semibold). */
  title?: React.ReactNode;
  /** Format hint under the title (14px regular). */
  description?: React.ReactNode;
  /** Brand-blue browse action label. */
  browseLabel?: React.ReactNode;
  /** Filled-state menu: replace-image action label. */
  editLabel?: string;
  /** Filled-state menu: delete-image action label. */
  removeLabel?: string;
  /** Native input id (label association). */
  id?: string;
  /** `aria-describedby` hint id for the dropzone button. */
  describedBy?: string;
  /** Invalid state (paints the error border). */
  invalid?: boolean;
  disabled?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  className?: string;
}

/**
 * Standalone file dropzone (Figma 732:18311 empty, 755:24553 filled) — the
 * presentational core behind `FormField.Upload`, for non-RHF callers (dialogs
 * with local file state).
 *
 * Empty state: the dashed #f9fafb dropzone with the 68px icon disc, the 20px
 * semibold title, the format hint, and the brand-blue "Browse File" action.
 * A picked single image swaps to the photo on cover with a floating Change /
 * Remove menu; anything else picked renders a file list under the dropzone.
 *
 * URL-first callers (server image + pick/remove callbacks) should use
 * `ImageUpload` instead — both render the same shared faces.
 */
export function Dropzone({
  value,
  onFiles,
  multiple = false,
  accept,
  maxBytes,
  onRejected,
  title = 'Drag & Drop File Here',
  description = 'Drag and drop your PNG, JPG, WebP, SVG images here or browse',
  browseLabel = 'Browse File',
  editLabel = 'Change',
  removeLabel = 'Remove',
  id,
  describedBy,
  invalid = false,
  disabled = false,
  onBlur,
  className,
}: DropzoneProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = React.useState(false);

  const files = toFileList(value);
  const singleImage = !multiple && files.length === 1 ? files[0] : undefined;
  const previewUrl = useObjectUrl(singleImage);
  const showPreview = singleImage !== undefined && previewUrl !== undefined;
  // A single non-image pick (PDF, video, …) has no preview — it renders as a
  // file row instead, so the selection never looks empty.
  const showList = files.length > 0 && !showPreview;

  const handleFiles = (list: FileList | null): void => {
    if (!list || list.length === 0) {
      onFiles([]);
      return;
    }
    const picked = Array.from(list);
    const { accepted, rejected } =
      maxBytes === undefined
        ? { accepted: picked, rejected: [] as string[] }
        : partitionBySize(picked, maxBytes);
    if (rejected.length > 0) onRejected?.(rejected);
    if (accepted.length === 0) {
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    onFiles(accepted);
  };

  const removeAt = (index: number): void => {
    onFiles(files.filter((_, i) => i !== index));
    if (inputRef.current) inputRef.current.value = '';
  };

  const openPicker = (): void => {
    inputRef.current?.click();
  };

  return (
    <div className={cn('grid gap-2', className)}>
      {showPreview && previewUrl !== undefined ? (
        <DropzoneFilledFace
          previewSrc={previewUrl}
          disabled={disabled}
          editLabel={editLabel}
          removeLabel={removeLabel}
          onReplace={openPicker}
          onRemove={() => removeAt(0)}
        />
      ) : (
        <DropzoneEmptyFace
          title={title}
          description={description}
          browseLabel={browseLabel}
          dragging={dragging}
          disabled={disabled}
          invalid={invalid}
          {...(describedBy !== undefined ? { describedBy } : {})}
          onOpen={openPicker}
          onDraggingChange={setDragging}
          onDropFiles={handleFiles}
        />
      )}

      <input
        ref={inputRef}
        id={id}
        type="file"
        className="sr-only"
        multiple={multiple}
        disabled={disabled}
        {...(accept ? { accept } : {})}
        onChange={(e) => handleFiles(e.target.files)}
        onBlur={onBlur}
      />

      {showList && (
        <ul className="grid gap-1.5" data-slot="dropzone-list">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center justify-between rounded-xl border border-input-border bg-card px-3 py-1.5 text-sm font-normal tracking-[-0.28px] text-input-fg shadow-field"
            >
              <span className="truncate">{file.name}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7"
                disabled={disabled}
                aria-label={`${removeLabel} ${file.name}`}
                onClick={() => removeAt(index)}
              >
                <XIcon />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Coerce a `File | File[] | null | undefined` value to a File[]. */
function toFileList(value: unknown): File[] {
  if (Array.isArray(value)) return value.filter((v): v is File => v instanceof File);
  if (value instanceof File) return [value];
  return [];
}

/**
 * Blob URL for a single picked image, revoked on change/unmount. Non-image
 * files render no preview — the caller falls back to the file list.
 *
 * The URL is cached across renders and recreated only when the file identity
 * changes. Blob URL creation/revocation happens in an effect because both are
 * side effects.
 */
function useObjectUrl(file: File | undefined): string | undefined {
  const [cached, setCached] = React.useState<{
    file: File | undefined;
    url: string | undefined;
  }>({ file: undefined, url: undefined });

  React.useEffect(() => {
    const isImage = file?.type.startsWith('image/') ?? false;
    const url = isImage && file ? URL.createObjectURL(file) : undefined;
    let active = true;

    queueMicrotask(() => {
      if (active) setCached({ file, url });
    });

    return () => {
      active = false;
      if (url !== undefined) URL.revokeObjectURL(url);
    };
  }, [file]);

  return cached.file === file ? cached.url : undefined;
}
